import { PlainRoute, RouteConfig } from 'react-router';
import { Store } from 'redux';

import { getRouteComponents } from 'ha/helpers/routes/getRouteComponents';
import { getFeatureFlags } from 'ha/modules/FeatureFlags';
import { getSelectedVariants } from 'ha/modules/Experiments/selectors';
import EXPERIMENTS from 'ha/constants/experiments';

import {
  onEnterMy,
  onEnterLangIndex,
  onEnterDefault,
  getIndexRouteWildcard,
  onEnterWildcard,
  onEnterSearchPage,
  getIndexRouteSearchPage,
  getIndexRouteLang,
  getChildRoutesLang,
  getCityCountry,
  onEnterPayoutMethods,
  redirectedRoutes,
  onEnterUserProfile,
  onEnterRoomPage,
  onEnterListYourPlace,
  onEnterAuthenticated,
  pricingPageRoute,
} from './utils/routes';
import AppRoot from './components/AppRoot';
import UrlResolver from './helpers/UrlResolver';
import { notEmpty } from './utils/notEmpty';

export default function getRoutes(
  store: Store,
  urlResolver: UrlResolver,
): RouteConfig {
  const getComponents = getRouteComponents(!process.browser)(store);

  const enterAuthenticated = onEnterAuthenticated(store);

  const { searchSubscriptionAccountStep } = getFeatureFlags(store.getState());
  const variant = getSelectedVariants(store.getState())[
    EXPERIMENTS.TNT388.name
  ];
  const subscriptionAccountStepEnabled =
    searchSubscriptionAccountStep === 'on' && variant === 'B';

  const routes: RouteConfig = [
    ...redirectedRoutes,
    {
      path: 'browser-not-supported',
      getComponents: getComponents('BrowserNotSupported'),
    },
    {
      path: 'login',
      getComponents: getComponents('Login'),
      onEnter: enterAuthenticated,
    },
    {
      path: 'register',
      getComponents: getComponents('Register'),
      onEnter: enterAuthenticated,
    },
    {
      path: 'register/:partnerId',
      getComponents: getComponents('RegisterPartner'),
      onEnter: enterAuthenticated,
    },
    pricingPageRoute(store, getComponents),
    {
      path: 's/:location(/)(:localizedKind)',
      onEnter: onEnterSearchPage(store),
      getIndexRoute: getIndexRouteSearchPage(store, getComponents),
    },
    {
      path: 'room/:id/:countryCode/:city(/)*',
      getComponents: getComponents('Listing'),
    },
    {
      path: 'reset-password/:token',
      getComponents: getComponents('ResetPassword'),
      onEnter: enterAuthenticated,
    },
    {
      path: 'users/show/:id',
      getComponents: getComponents('UserPage'),
    },
    {
      path: 'activate-account/:token',
      getComponents: getComponents('ActivateAccount'),
    },
    {
      path: 'answers',
      getComponents: getComponents('Answers'),
    },
    {
      path: 'adyenPaymentResult',
      getComponents: getComponents('AdyenPaymentResult'),
      onEnter: onEnterMy(store),
    },
    {
      path: 'contact',
      getComponents: getComponents('ContactUs'),
    },
    {
      path: 'cookie-policy',
      getComponents: getComponents('CookiePolicy'),
    },
    {
      path: 'data-security',
      getComponents: getComponents('DataSecurity'),
    },
    {
      path: 'legal',
      getComponents: getComponents('Legal'),
    },
    {
      path: 'list-your-place',
      getComponents: getComponents('ListYourPlace'),
      onEnter: onEnterListYourPlace(store),
    },
    {
      path: 'partner-redirect',
      getComponents: getComponents('PartnerRedirect'),
    },
    {
      path: 'recover-account',
      getComponents: getComponents('RecoverAccount'),
      onEnter: enterAuthenticated,
    },
    {
      path: 'safe-payment-service',
      getComponents: getComponents('SafePaymentService'),
    },
    {
      path: 'sms-accept/:token',
      getComponents: getComponents('SmsAccept'),
    },
    {
      path: 'de/agb',
      getComponents: getComponents('Terms'),
    },
    {
      path: 'terms',
      getComponents: getComponents('Terms'),
    },
    {
      path: 'universities',
      getComponents: getComponents('Universities'),
    },
    {
      path: 'sitemap',
      getComponents: getComponents('Sitemap'),
    },
    {
      path: 'under-review',
      getComponents: getComponents('UnderReview'),
    },
    {
      path: 'auth/facebook/callback',
      getComponents: getComponents('FacebookLogin'),
    },
    {
      path: 'auth/google/callback',
      getComponents: getComponents('GoogleLogin'),
    },
    {
      path: 'h/:token',
      getComponents: getComponents('Shortlink'),
    },
    {
      path: 'payment-plan/:token(/)(:state)',
      getComponents: getComponents('PaymentPlanAccept'),
    },
    {
      path: 'billing-subscription',
      indexRoute: {
        getComponents: getComponents('BillingSubscriptionLanding'),
      },
      childRoutes: [
        {
          path: 'activate',
          getComponents: getComponents('BillingSubscriptionActivate'),
          onEnter: subscriptionAccountStepEnabled
            ? undefined
            : onEnterMy(store),
        },
      ],
    },
    {
      path: 'my',
      indexRoute: {
        getComponents: getComponents('UserDashboard'),
      },
      onEnter: onEnterMy(store),
      childRoutes: [
        {
          path: 'book/contact/:listingId(/)(:currentStep)',
          getComponents: getComponents('ContactAdvertiser'),
        },
        {
          path: 'inbox',
          getComponents: getComponents('Inbox'),
        },
        {
          path: 'bookings',
          getComponents: getComponents('Bookings'),
        },
        {
          path: 'listings',
          getComponents: getComponents('Listings'),
        },
        {
          path: 'listings/settings',
          getComponents: getComponents('ListingsSharedSettings'),
        },
        {
          path: 'listings/:id/edit',
          getComponents: getComponents('ListingEdit'),
        },
        {
          path: 'listings/:id/edit-draft',
          getComponents: getComponents('ListingEditDraft'),
        },
        {
          path: 'listings/:id/success',
          getComponents: getComponents('ShareRoom'),
        },
        {
          path: 'listings/:id/calendar',
          getComponents: getComponents('ListingCalendar'),
        },
        {
          path: 'profile/edit',
          getComponents: getComponents('ProfileEdit'),
        },
        {
          path: 'talk/:id',
          getComponents: getComponents('Talk'),
        },
        {
          path: 'talk/:id/:mode',
          getComponents: getComponents('Booking'),
        },
        {
          path: 'get-verified',
          getComponents: getComponents('Verification'),
        },
        {
          path: 'verify-phone-number',
          getComponents: getComponents('VerifyPhoneNumber'),
        },
        {
          path: 'share-room/:id',
          getComponents: getComponents('ShareRoom'),
        },
        {
          path: 'favorites',
          getComponents: getComponents('FavoriteListings'),
        },
        {
          path: 'searches',
          getComponents: getComponents('SearchAlertListings'),
        },
        {
          path: 'surveys/:id',
          getComponents: getComponents('SurveyReview'),
        },
        {
          path: 'subscriptions',
          getComponents: getComponents('Subscriptions'),
        },
        {
          path: 'profile/:userID/confirm/:token',
          getComponents: getComponents('ConfirmProfile'),
        },
        {
          path: 'profile/:userID/pm/confirm/:token',
          getComponents: getComponents('ConfirmPayoutMethod'),
        },
        {
          path: 'profile/:userID',
          onEnter: onEnterUserProfile,
        },
        {
          path: 'callback/create-search-alert',
          getComponents: getComponents('ConfirmSearchAlert'),
        },
        {
          path: 'payout-methods',
          onEnter: onEnterPayoutMethods(urlResolver),
        },
        {
          path: 'pay/:handle',
          getComponents: getComponents('PayPaymentRequest'),
        },
        {
          path: 'payments',
          indexRoute: {
            getComponents: getComponents('PaymentMethods'),
          },
          childRoutes: [
            {
              path: 'methods',
              getComponents: getComponents('PaymentMethods'),
            },
            {
              getComponents: getComponents('PaymentsPaymentRequests'),
              path: 'requests',
            },
          ],
        },
        {
          path: 'payouts',
          indexRoute: {
            getComponents: getComponents('PayoutsPreferences'),
          },
          childRoutes: [
            {
              path: 'preferences',
              getComponents: getComponents('PayoutsPreferences'),
            },
            {
              path: 'preferences/:payoutMethodId/routing',
              getComponents: getComponents('PayoutRouting'),
            },
            {
              path: 'history',
              getComponents: getComponents('PayoutsHistory'),
            },
            {
              path: 'invoices',
              getComponents: getComponents('PayoutsInvoices'),
            },
            {
              path: 'plan',
              getComponents: getComponents('PayoutsPlan'),
            },
            {
              path: 'billing',
              getComponents: getComponents('PayoutsBilling'),
            },
            {
              path: 'payment-requests',
              getComponents: getComponents('PayoutsPaymentRequests'),
            },
            {
              path: 'verify-identity',
              getComponents: getComponents('VerifyIdentity'),
            },
          ],
        },
        {
          path: 'payment-plan(/:conversationId)',
          getComponents: getComponents('PaymentPlan'),
        },
      ].filter(notEmpty),
    },
    {
      path: ':city--:country',
      getComponents: getCityCountry(store, getComponents),
    },
    {
      path: 'r/pay/:handle',
      getComponents: getComponents('PayPaymentRequest'),
      onEnter: onEnterMy(store),
    },
    {
      path: 'r/*',
      onEnter: onEnterRoomPage,
    },
    {
      path: 'link/:listingIdEncoded',
      getComponents: getComponents('BookingLink'),
    },
  ]
    .filter(notEmpty)
    .map(route => ({
      ...route,
      onEnter: 'onEnter' in route ? route.onEnter : onEnterDefault,
    }));

  return {
    path: '/',
    component: AppRoot,
    indexRoute: {
      getComponents: getComponents('Home'),
    },
    childRoutes: [
      ...(routes as RouteConfig as Array<PlainRoute>),
      {
        path: ':lang/',
        onEnter: onEnterLangIndex,
        getIndexRoute: getIndexRouteLang(getComponents, 'Home'),
        getChildRoutes: getChildRoutesLang(routes),
      },
      {
        path: '*',
        onEnter: onEnterWildcard(store),
        // react-router calls the callback provided to `getIndexRoute` twice
        // when it returns a promise
        // @link http://goo.gl/6gT7Y8
        getIndexRoute: getIndexRouteWildcard(store, getComponents),
      },
    ],
  };
}
